import { useRef, useState } from "react";
import { ChevronLeft, ChevronRight, GripVertical, ImagePlus, Loader2, X } from "lucide-react";
import { LayoutGroup, motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiError, imageUrl } from "@/lib/api";

export async function uploadImage(file, purpose = "general") {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/uploads?purpose=${purpose}`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

const move = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

export const ImageUploader = ({ value = [], onChange, max = 8, testId = "image-uploader", purpose = "general", coverBadge = true }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);

  const onFiles = async (files) => {
    const list = Array.from(files || []).slice(0, max - value.length);
    if (!list.length) return;
    setBusy(true);
    const added = [];
    for (const f of list) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} is larger than 8MB`); continue; }
      try { added.push(await uploadImage(f, purpose)); } catch (e) { toast.error(apiError(e)); }
    }
    onChange([...value, ...added]);
    setBusy(false);
    if (input.current) input.current.value = "";
  };

  const drop = (to) => {
    if (drag !== null) onChange(move(value, drag, to));
    setDrag(null); setOver(null);
  };
  const canReorder = value.length > 1;

  return (
    <LayoutGroup id={testId}>
      <div data-testid={testId} className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {value.map((p, i) => (
          <motion.div key={p} layout transition={{ type: "spring", stiffness: 500, damping: 40 }}
            draggable={canReorder} onDragStart={(e) => { setDrag(i); e.dataTransfer.effectAllowed = "move"; }}
            onDragOver={(e) => { e.preventDefault(); if (over !== i) setOver(i); }} onDragLeave={() => setOver(null)}
            onDrop={(e) => { e.preventDefault(); drop(i); }} onDragEnd={() => { setDrag(null); setOver(null); }}
            data-testid={`${testId}-item-${i}`}
            className={`group relative aspect-[4/3] overflow-hidden rounded-lg border ${over === i && drag !== i ? "border-primary ring-2 ring-primary/40" : "border-border"} ${drag === i ? "opacity-50" : ""} ${canReorder ? "cursor-grab active:cursor-grabbing" : ""}`}>
            <img src={imageUrl(p)} alt={`Photo ${i + 1}`} className="img-cover pointer-events-none select-none" draggable={false} />
            {coverBadge && i === 0 && <span className="absolute bottom-2 left-2 rounded bg-background/80 px-2 py-0.5 text-xs">Cover</span>}
            {canReorder && <span className="absolute left-2 top-2 hidden h-9 w-9 items-center justify-center rounded-full bg-background/80 text-muted-foreground sm:flex sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100" aria-hidden="true"><GripVertical className="h-4 w-4" /></span>}
            <button type="button" aria-label={`Remove photo ${i + 1}`} data-testid={`${testId}-remove-${i}`} onClick={() => onChange(value.filter((x) => x !== p))}
              className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-background/80 hover:bg-background">
              <X className="h-4 w-4" />
            </button>
            {canReorder && (
              <div className="absolute inset-x-2 bottom-2 flex justify-between sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:focus-within:opacity-100">
                <button type="button" disabled={i === 0} aria-label={`Move photo ${i + 1} left`} data-testid={`${testId}-left-${i}`} onClick={() => onChange(move(value, i, i - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background/80 hover:bg-background disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                <button type="button" disabled={i === value.length - 1} aria-label={`Move photo ${i + 1} right`} data-testid={`${testId}-right-${i}`} onClick={() => onChange(move(value, i, i + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-background/80 hover:bg-background disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </motion.div>
        ))}
        {value.length < max && (
          <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testId}-add`}
            className="flex aspect-[4/3] flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/30 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            {busy ? "Uploading" : "Add photo"}
          </button>
        )}
        <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid={`${testId}-input`} />
      </div>
      {canReorder && <p className="mt-2 text-xs text-muted-foreground" data-testid={`${testId}-reorder-hint`}>Drag photos to reorder, or use the arrows. The first photo leads.</p>}
    </LayoutGroup>
  );
};
